import { makeStyles } from 'tss-react/mui';
import React, { useContext } from 'react';
import { NodeRendererProps } from 'react-arborist/dist/types';
import { ArboristTreeContext } from '../../Components/Urval/Components/UrvalFieldPicker/Components/ArboristTreeNodeContext';
import EDTreeSelectListItemBase from './EDTreeSelectListItemBase';
import {
  getSublistCheckStatus,
  ITreeSection,
  SectionCheckStatus,
  updateChecked,
  updateOpenVirtualTree,
} from './TreeSelectUtils';

interface IProps<T> extends NodeRendererProps<ITreeSection<T>> {}

const useStyles = makeStyles()(theme => ({
  nodeLine: {
    borderLeft: `2px solid #d7d7d7`,
  },
}));

const EDVirtualTreeSelectListItem = <T,>(props: IProps<T>) => {
  const { classes, cx } = useStyles();
  const arboristTreeContext = useContext(ArboristTreeContext);
  const { disableSectionsClick, sections, sectionsUpdate } =
    arboristTreeContext;

  const onCheck = (suggestion: ITreeSection<T>) => (value: boolean) => {
    const { updatedSections, checkedSections } = updateChecked(
      props.data.parentNode ?? suggestion,
      arboristTreeContext.sections,
      value,
      suggestion
    );
    if (arboristTreeContext.onCheck) {
      arboristTreeContext.sectionsUpdate(
        arboristTreeContext.onCheck(
          updatedSections,
          checkedSections,
          value,
          suggestion
        )
      );
    } else {
      arboristTreeContext.sectionsUpdate(updatedSections);
    }
  };

  const sectionClickIsDisabled = React.useCallback(
    (suggestion: ITreeSection<T>) => {
      if (!suggestion.subList) {
        return false;
      }
      if (typeof disableSectionsClick === 'function') {
        return disableSectionsClick(suggestion);
      }
      return Boolean(disableSectionsClick);
    },
    [disableSectionsClick]
  );

  const updateOpenSection = React.useCallback(
    (section: ITreeSection) => () => {
      sectionsUpdate(updateOpenVirtualTree(section, sections));
    },
    [sections, sectionsUpdate]
  );

  const disableSectionClick = sectionClickIsDisabled(props.data);

  const sublistCheckStatus = getSublistCheckStatus(props.data);

  return (
    <div
      style={{
        ...props.styles.row,
        padding: '0 24px',
        width: `calc(100% - 48px)`,
      }}
    >
      <div
        style={props.styles.indent}
        className={cx({
          [classes.nodeLine]: (props.data.level ?? 0) > 0,
        })}
      >
        <EDTreeSelectListItemBase
          parentSection={props.data.parentNode}
          section={props.data}
          showPointerLines={arboristTreeContext.showPointerLines}
          level={props.data.level ?? 0}
          checked={sublistCheckStatus === SectionCheckStatus.FullyChecked}
          indeterminate={
            sublistCheckStatus === SectionCheckStatus.PartiallyChecked
          }
          checkBoxDisabled={disableSectionClick || props.data.disabled?.value}
          checkBoxDisabledReason={
            disableSectionClick ? 'Nod ej valbar' : props.data.disabled?.tooltip
          }
          checkBoxTooltip={
            props.data.subList
              ? sublistCheckStatus === SectionCheckStatus.FullyChecked
                ? 'Ta bort alla'
                : 'Fyll i alla'
              : undefined
          }
          checkboxNoClickPropagation={
            props.data.open || props.data.checked || !props.data.subList
          }
          fontBold={
            arboristTreeContext.parentSectionBoldText &&
            Boolean(props.data.subList)
          }
          searchValue={arboristTreeContext.searchValue}
          onListItemClick={props.data.subList && updateOpenSection(props.data)}
          onCheck={!disableSectionClick ? onCheck(props.data) : undefined}
          renderSecondaryText={arboristTreeContext.renderSecondaryText}
          showListItemButtons={arboristTreeContext.showListItemButtons}
          hoverChecklistButtons={arboristTreeContext.hoverChecklistButtons}
          renderListItemButtons={arboristTreeContext.renderListItemButtons}
        />
      </div>
    </div>
  );
};

export default EDVirtualTreeSelectListItem;
